import { z } from 'zod';
import { COMMENT_MAX_BLOCKS, COMMENT_MAX_INLINE_HTML_LEN, COMMENT_MAX_PLAINTEXT_LEN, } from '../primitives/editor-block.js';
export const SURVEY_LINKABLE_KIND = 'survey';
export const SurveyStatus = z.enum(['draft', 'scheduled', 'live', 'closed', 'archived']);
export const SurveyResponseOwnershipMode = z.enum(['individual', 'per_child']);
export const SurveyQuestionKind = z.enum([
    'short',
    'long',
    'single-choice',
    'multi-choice',
    'dropdown',
    'rating-1-5',
    'rating-1-10',
    'nps',
    'matrix',
    'date',
    'time',
    'file-upload',
    'image-choice',
    'ranking',
    'slider',
    'availability-coordination',
    'section',
]);
export const SurveyGradingMethod = z.enum(['exact', 'numeric', 'ai']);
export const SURVEY_MAX_EDITOR_BLOCKS = Math.min(COMMENT_MAX_BLOCKS, 16);
export const SURVEY_MAX_OPTIONS = 100;
export const SURVEY_MAX_QUESTIONS = 200;
export const SurveyEditorBlock = z.lazy(() => z
    .object({
    id: z.string().min(1).max(64),
    type: z.string().min(1).max(64),
    content: z.string().max(COMMENT_MAX_INLINE_HTML_LEN),
    html: z.string().max(COMMENT_MAX_INLINE_HTML_LEN).optional(),
    attributes: z.record(z.string(), z.unknown()),
    children: z.array(SurveyEditorBlock).max(SURVEY_MAX_EDITOR_BLOCKS).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough());
export const SurveyEditorBlocks = z
    .array(SurveyEditorBlock)
    .min(1)
    .max(SURVEY_MAX_EDITOR_BLOCKS);
export const SurveyOptionInput = z
    .object({
    id: z.string().min(1).max(64).optional(),
    value: z.string().min(1).max(120).optional(),
    label: SurveyEditorBlocks,
    imageFileId: z.string().uuid().optional(),
    imageAlt: z.string().max(255).optional(),
})
    .strict();
export const SurveyMatrixAxisInput = z
    .object({
    id: z.string().min(1).max(64).optional(),
    value: z.string().min(1).max(120).optional(),
    label: SurveyEditorBlocks,
})
    .strict();
export const SurveyBranchRule = z
    .object({
    optionValue: z.string().min(1).max(120).optional(),
    equals: z.unknown().optional(),
    targetQuestionKey: z.string().min(1).max(80).optional(),
    end: z.boolean().optional(),
})
    .strict();
export const SurveyDisplayLogicRule = z
    .object({
    sourceQuestionKey: z.string().min(1).max(80),
    optionValues: z.array(z.string().min(1).max(120)).min(1).max(SURVEY_MAX_OPTIONS),
})
    .strict();
export const SurveyDisplayLogic = z
    .object({
    mode: z.enum(['all', 'any']),
    rules: z.array(SurveyDisplayLogicRule).min(1).max(16),
})
    .strict();
export const SurveyQuestionInput = z
    .object({
    key: z.string().min(1).max(80).optional(),
    kind: SurveyQuestionKind,
    prompt: SurveyEditorBlocks,
    helpText: SurveyEditorBlocks.nullable().optional(),
    required: z.boolean().optional(),
    options: z.array(SurveyOptionInput).max(SURVEY_MAX_OPTIONS).optional(),
    rows: z.array(SurveyMatrixAxisInput).max(SURVEY_MAX_OPTIONS).optional(),
    columns: z.array(SurveyMatrixAxisInput).max(SURVEY_MAX_OPTIONS).optional(),
    validation: z.record(z.string(), z.unknown()).optional(),
    branchingRules: z.array(SurveyBranchRule).max(32).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    answerKey: z.unknown().optional(),
    answerExplanation: SurveyEditorBlocks.nullable().optional(),
    points: z.number().int().min(0).max(1000).optional(),
})
    .strict();
export const SurveyQuestion = z.object({
    id: z.string().uuid(),
    surveyId: z.string().uuid(),
    key: z.string().nullable(),
    position: z.number().int().nonnegative(),
    kind: SurveyQuestionKind,
    prompt: SurveyEditorBlocks,
    promptText: z.string(),
    helpText: SurveyEditorBlocks.nullable(),
    helpTextText: z.string().nullable(),
    required: z.boolean(),
    options: z.array(SurveyOptionInput),
    rows: z.array(SurveyMatrixAxisInput),
    columns: z.array(SurveyMatrixAxisInput),
    validation: z.record(z.string(), z.unknown()),
    branchingRules: z.array(SurveyBranchRule),
    settings: z.record(z.string(), z.unknown()),
    answerKey: z.unknown().nullable(),
    answerExplanation: SurveyEditorBlocks.nullable(),
    answerExplanationText: z.string().nullable(),
    points: z.number().int().nonnegative(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const SurveyAnswerInput = z
    .object({
    questionId: z.string().uuid(),
    value: z.any(),
})
    .strict();
export const SurveyFileUploadAnswer = z
    .object({
    fileId: z.string().uuid(),
    name: z.string().min(1).max(255),
    sizeBytes: z.number().int().nonnegative().optional(),
    mimeType: z.string().min(1).max(120).optional(),
})
    .strict();
export const SurveyAnswer = z.object({
    responseId: z.string().uuid(),
    questionId: z.string().uuid(),
    value: z.any(),
    isCorrect: z.boolean().nullable(),
    earnedPoints: z.number().int().nonnegative().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const SurveyResponseIdentity = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
});
export const SurveyResponseParticipantRole = z.enum(['respondent', 'subject']);
export const SurveyResponseParticipant = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    roles: z.array(SurveyResponseParticipantRole).min(1),
    responseCount: z.number().int().nonnegative(),
});
export const Survey = z.object({
    id: z.string().uuid(),
    title: z.string(),
    description: SurveyEditorBlocks.nullable(),
    descriptionText: z.string(),
    audienceGroupId: z.string().uuid().nullable(),
    anonymous: z.boolean(),
    responseOwnershipMode: SurveyResponseOwnershipMode,
    oneResponsePerUser: z.boolean(),
    maxResponsesPerUser: z.number().int().positive().nullable(),
    requireLoginToSubmit: z.boolean(),
    showSubmittedAnswers: z.boolean(),
    publicResults: z.boolean(),
    quizMode: z.boolean(),
    shuffleQuestions: z.boolean(),
    opensAt: z.string().datetime({ offset: true }).nullable(),
    closesAt: z.string().datetime({ offset: true }).nullable(),
    responseCap: z.number().int().positive().nullable(),
    status: SurveyStatus,
    ownerId: z.string().uuid().nullable(),
    responseCount: z.number().int().nonnegative(),
    viewerCanWrite: z.boolean().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    closedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
});
export const SurveySubmissionSubject = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    role: z.enum(['self', 'child']),
    hasSubmitted: z.boolean(),
    remainingSubmissions: z.number().int().nonnegative().nullable(),
});
export const SurveyViewerSubmissionContext = z.object({
    mode: SurveyResponseOwnershipMode,
    eligibleSubjects: z.array(SurveySubmissionSubject),
    defaultSubjectUserId: z.string().uuid().nullable(),
});
export const SurveyDetail = Survey.extend({
    questions: z.array(SurveyQuestion),
    viewerSubmissionContext: SurveyViewerSubmissionContext,
});
export const SurveyResponse = z.object({
    id: z.string().uuid(),
    surveyId: z.string().uuid(),
    respondentId: z.string().uuid().nullable(),
    subjectUserId: z.string().uuid().nullable(),
    respondentIdentity: SurveyResponseIdentity.nullable(),
    subjectIdentity: SurveyResponseIdentity.nullable(),
    responseOwnershipMode: SurveyResponseOwnershipMode,
    startedAt: z.string().datetime({ offset: true }).nullable(),
    completedAt: z.string().datetime({ offset: true }),
    durationMs: z.number().int().nonnegative().nullable(),
    earnedPoints: z.number().int().nonnegative(),
    maxPoints: z.number().int().nonnegative(),
    answers: z.array(SurveyAnswer),
});
export const SurveyAnswerGrade = z.object({
    questionId: z.string().uuid(),
    correct: z.boolean().nullable(),
    earnedPoints: z.number().int().nonnegative(),
    maxPoints: z.number().int().nonnegative(),
    answerKey: z.unknown().nullable(),
    explanation: SurveyEditorBlocks.nullable(),
    explanationText: z.string().nullable(),
    gradingMethod: SurveyGradingMethod,
    gradingRationale: z.string().nullable(),
});
export const SurveyResponseGrading = z.object({
    earnedPoints: z.number().int().nonnegative(),
    maxPoints: z.number().int().nonnegative(),
    percentage: z.number().nullable(),
    answers: z.array(SurveyAnswerGrade),
});
export const SurveyQuestionAggregate = z.object({
    questionId: z.string().uuid(),
    kind: SurveyQuestionKind,
    responseCount: z.number().int().nonnegative(),
    applicableCount: z.number().int().nonnegative(),
    answeredCount: z.number().int().nonnegative(),
    unansweredCount: z.number().int().nonnegative(),
    hiddenCount: z.number().int().nonnegative(),
    counts: z.record(z.string(), z.number().int().nonnegative()),
    average: z.number().nullable(),
});
export const SurveyEmbed = z.object({
    articleId: z.string().uuid(),
    surveyId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export { COMMENT_MAX_INLINE_HTML_LEN, COMMENT_MAX_PLAINTEXT_LEN };
//# sourceMappingURL=survey.js.map